/**
 * What "/" shows, and to whom.
 *
 * The marketing page and the product share one address. A visitor who has never signed in needs the
 * marketing page. Somebody who already has an account needs their workspace, and had been getting
 * the marketing page instead: every launch of the installed app opened on "Describe it. Nuvra builds
 * and ships it." and a Start Building button, for a customer who had already started building. That
 * is the single most repeated annoyance in the product, because it happens on every launch.
 *
 * The subtlety is that "/" cannot simply wait for the auth answer the way /login and /signup do.
 * Those are private, so blocking on a spinner costs nothing. "/" is the public marketing page, and
 * making every anonymous visitor and every crawler wait behind a session check to see the product's
 * front door is a real cost paid by people who do not have a session at all.
 *
 * So the wait is taken only where there is a reason to expect a session:
 *
 *   - native, always. There is no crawler inside the app and no marketing case for showing an
 *     account holder a signup pitch, so the correct answer is worth waiting for.
 *   - the web, only when a persisted session token is actually present. Supabase keeps it under
 *     `sb-<project>-auth-token`, readable synchronously, so a returning customer is recognised
 *     before the first paint and never sees the landing page flash past.
 *   - the web with no token: nothing is blocked at all, and the marketing page renders exactly as
 *     it always did.
 *
 * The token is used ONLY to decide whether waiting is worthwhile. It is never treated as proof of
 * anything: `session` from the real auth context is what actually routes, so an expired or forged
 * token buys a spinner and then the landing page, never the workspace.
 */

import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "./AuthContext";
import { isNative } from "@/shell/platform";
import LandingPage from "@/pages/LandingPage";

/**
 * Is there a persisted Supabase session on this device?
 *
 * A hint, not an authority. Pure and exported so the decision can be tested without a browser.
 */
export function hasPersistedSessionToken(storage: Storage | undefined | null): boolean {
  if (!storage) return false;
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      // The shape supabase-js writes when no custom storageKey is configured, which is our case.
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) return true;
    }
  } catch {
    // Private browsing, blocked site data, or a storage quota error. Not knowing is the same as no
    // hint: fall through and let the landing page render rather than blocking on a guess.
  }
  return false;
}

/** Should the answer be waited for, rather than rendering marketing and correcting later? */
export function shouldAwaitSession(native: boolean, tokenPresent: boolean): boolean {
  return native || tokenPresent;
}

export function HomeRoute() {
  const { session, loading } = useAuth();

  if (loading && shouldAwaitSession(isNative(), hasPersistedSessionToken(typeof window === "undefined" ? null : window.localStorage))) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // The real session is the only thing that routes anybody anywhere.
  if (session) return <Navigate to="/dashboard" replace />;

  return <LandingPage />;
}
